import React from 'react'
import { useTranslation } from 'react-i18next'

import { EditOrganizationDetailsModalProps } from '../OverviewDetails.types'

import { useMutation, useRequiredParams } from '@percent/admin-dashboard/common/hooks'
import { AcknowledgeModal, ActionModal, Modal, Spacer, Text } from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'

const GOOGLE_DEACTIVATION_CONFIRMATION_REQUIRED = 'organisation/google_deactivation_confirmation_required'

export function EditRegistrationStatusModal({
  open,
  onClose,
  organization,
  refresh
}: EditOrganizationDetailsModalProps) {
  const { id } = useRequiredParams()
  const { adminService } = useServices()

  const [{ error, errorMessage, success, isLoading }, { apiFunc, setError, setErrorMessage }] = useMutation(
    adminService.editOrganisation
  )
  const { t } = useTranslation()

  const handleRegistrationActive = () => {
    return apiFunc({
      payload: { registrationActive: !organization.registrationActive },
      id
    })
  }

  const handleConfirmGoogleDeactivation = () => {
    return apiFunc({
      payload: { registrationActive: false, confirmGoogleDeactivation: true },
      id
    })
  }

  const handleCloseModal = () => {
    // Closing mid-request would skip the refresh once the PATCH lands, leaving a stale status on the page.
    if (isLoading) {
      return
    }

    setError('')
    setErrorMessage('')
    onClose()
    refresh()
  }

  const registrationStatusText =
    organization.registrationActive === true
      ? t('typography.deactivateOrganization')
      : t('typography.activateOrganization')

  const orgRegistrationStatus =
    organization.registrationActive === true ? t('typography.deactivated') : t('typography.activated')

  const successModal = success && (
    <AcknowledgeModal
      result="positive"
      title={t('dialog.editRegistrationModal.success.title', {
        registrationStatus: orgRegistrationStatus
      })}
      description={`${organization.name} ${t('dialog.editRegistrationModal.success.description', {
        registrationStatus: orgRegistrationStatus
      })}`}
      buttonText={t('button.close')}
      handleClose={handleCloseModal}
    />
  )
  const googleConfirmationModal = error?.code === GOOGLE_DEACTIVATION_CONFIRMATION_REQUIRED && (
    <ActionModal
      title={t('dialog.editRegistrationModal.googleConfirmation.title')}
      primaryButtonText={t('dialog.editRegistrationModal.googleConfirmation.confirm')}
      secondaryButtonText={t('button.cancel')}
      type="submit"
      loading={isLoading}
      handleClose={handleCloseModal}
      handleSubmit={handleConfirmGoogleDeactivation}
      primaryBtnTestId="confirm-google-deactivation-submit-button"
      secondaryBtnTestId="confirm-google-deactivation-cancel-button"
    >
      <Text size="small">
        {t('dialog.editRegistrationModal.googleConfirmation.description', {
          organizationName: organization.name
        })}
      </Text>
      <Spacer axis="vertical" size={6} />
    </ActionModal>
  )
  const errorModal = errorMessage && (
    <AcknowledgeModal
      result="negative"
      title={t('dialog.editRegistrationModal.error.title', {
        registrationStatus: orgRegistrationStatus
      })}
      description={t('dialog.editRegistrationModal.error.description', {
        registrationStatus: orgRegistrationStatus
      })}
      buttonText={t('button.close')}
      handleClose={handleCloseModal}
    />
  )

  return (
    <Modal open={open} onClose={handleCloseModal} aria-labelledby="bank-details-form-modal">
      {successModal || googleConfirmationModal || errorModal || (
        <ActionModal
          title={registrationStatusText}
          primaryButtonText={registrationStatusText}
          secondaryButtonText={t('button.close')}
          type="submit"
          loading={isLoading}
          handleClose={handleCloseModal}
          handleSubmit={handleRegistrationActive}
          primaryBtnTestId="edit-registration-status-submit-button"
          secondaryBtnTestId="edit-registration-status-cancel-button"
        >
          <Text size="small">
            {organization.registrationActive === true
              ? t('dialog.editRegistrationModal.deactivate', {
                  organizationName: organization.name
                })
              : t('dialog.editRegistrationModal.activate', {
                  organizationName: organization.name
                })}
          </Text>
          <Spacer axis="vertical" size={6} />
        </ActionModal>
      )}
    </Modal>
  )
}
